import React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Filter, X } from "lucide-react";

interface SocieteRanking {
  societe_id: string;
  societe_nom: string;
  nb_candidatures: number;
  score_moyen: number;
  decision_counts: {
    shortlist: number;
    consider: number;
    reject: number;
  };
  candidats: Array<{
    id: string;
    nom: string;
    score: number;
    decision: string;
    created_at: string;
  }>;
  scores_criteres: { [key: string]: { score: number; count: number } };
}

interface ComparisonFiltersProps {
  rankings: SocieteRanking[];
  selectedCompanies: string[];
  selectedCriteria: string[];
  visualizationType: string;
  filterMode: 'top' | 'manual' | 'decision';
  topCount: number;
  selectedDecisions: string[];
  onCompaniesChange: (companies: string[]) => void;
  onCriteriaChange: (criteria: string[]) => void;
  onVisualizationTypeChange: (type: string) => void;
  onFilterModeChange: (mode: 'top' | 'manual' | 'decision') => void;
  onTopCountChange: (count: number) => void;
  onDecisionsChange: (decisions: string[]) => void;
}

export const ComparisonFilters: React.FC<ComparisonFiltersProps> = ({
  rankings,
  selectedCompanies,
  selectedCriteria,
  visualizationType,
  filterMode,
  topCount,
  selectedDecisions,
  onCompaniesChange,
  onCriteriaChange,
  onVisualizationTypeChange,
  onFilterModeChange,
  onTopCountChange,
  onDecisionsChange
}) => {
  // Extract all available criteria from the analyses
  const allCriteria = Array.from(
    new Set(rankings.flatMap(r => Object.keys(r.scores_criteres)))
  );
  
  const criteriaOptions = [
    { key: 'overall_score', label: 'Score global', type: 'global' },
    ...allCriteria.map(c => ({ key: `criteria_${c}`, label: c, type: 'criteria' }))
  ];

  const removeCompany = (companyId: string) => {
    onCompaniesChange(selectedCompanies.filter(id => id !== companyId));
  };

  const removeCriteria = (criteriaKey: string) => {
    onCriteriaChange(selectedCriteria.filter(key => key !== criteriaKey));
  };

  const handleCompanyToggle = (companyId: string, checked: boolean) => {
    if (checked) {
      onCompaniesChange([...selectedCompanies, companyId]);
    } else {
      onCompaniesChange(selectedCompanies.filter(id => id !== companyId));
    }
  };

  const handleCriteriaToggle = (criteriaKey: string, checked: boolean) => {
    if (checked) {
      onCriteriaChange([...selectedCriteria, criteriaKey]);
    } else {
      onCriteriaChange(selectedCriteria.filter(key => key !== criteriaKey));
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5" />
          Filtres de Comparaison
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Visualization Type */}
        <div className="space-y-2">
          <Label>Type de visualisation</Label>
          <Select value={visualizationType} onValueChange={onVisualizationTypeChange}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="radar">Graphique Radar</SelectItem>
              <SelectItem value="heatmap">Matrice de Chaleur</SelectItem>
              <SelectItem value="bar">Graphique en Barres</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Company Selection Mode */}
        <div className="space-y-2">
          <Label>Mode de sélection des sociétés</Label>
          <div className="flex gap-2">
            <Button
              variant={filterMode === 'top' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onFilterModeChange('top')}
            >
              Top N
            </Button>
            <Button
              variant={filterMode === 'decision' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onFilterModeChange('decision')}
            >
              Par décision
            </Button>
            <Button
              variant={filterMode === 'manual' ? 'default' : 'outline'}
              size="sm"
              onClick={() => onFilterModeChange('manual')}
            >
              Manuel
            </Button>
          </div>
        </div>

        {/* Top N Selection */}
        {filterMode === 'top' && (
          <div className="space-y-2">
            <Label>Nombre de sociétés (Top N)</Label>
            <Select value={topCount.toString()} onValueChange={(value) => onTopCountChange(parseInt(value))}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[3, 5, 8, 10, 15].map(n => (
                  <SelectItem key={n} value={n.toString()}>Top {n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Decision Filter */}
        {filterMode === 'decision' && (
          <div className="space-y-2">
            <Label>Filtrer par décisions</Label>
            <div className="space-y-2">
              {['shortlist', 'consider', 'reject'].map(decision => (
                <div key={decision} className="flex items-center space-x-2">
                  <Checkbox
                    id={decision}
                    checked={selectedDecisions.includes(decision)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        onDecisionsChange([...selectedDecisions, decision]);
                      } else {
                        onDecisionsChange(selectedDecisions.filter(d => d !== decision));
                      }
                    }}
                  />
                  <Label htmlFor={decision} className="capitalize">
                    {decision === 'shortlist' ? 'Shortlist' : 
                     decision === 'consider' ? 'Consider' : 'Reject'}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Manual Company Selection */}
        {filterMode === 'manual' && (
          <div className="space-y-2">
            <Label>Sélection manuelle des sociétés</Label>
            <ScrollArea className="h-40 border rounded-md p-2">
              <div className="space-y-2">
                {rankings.map(ranking => (
                  <div key={ranking.societe_id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`company-${ranking.societe_id}`}
                      checked={selectedCompanies.includes(ranking.societe_id)}
                      onCheckedChange={(checked) => handleCompanyToggle(ranking.societe_id, !!checked)}
                    />
                    <Label htmlFor={`company-${ranking.societe_id}`} className="text-sm">
                      {ranking.societe_nom} ({ranking.score_moyen.toFixed(1)}%)
                    </Label>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Selected Companies Display */}
        {selectedCompanies.length > 0 && (
          <div className="space-y-2">
            <Label>Sociétés sélectionnées ({selectedCompanies.length})</Label>
            <div className="flex flex-wrap gap-2">
              {selectedCompanies.map(companyId => {
                const company = rankings.find(r => r.societe_id === companyId);
                return company ? (
                  <Badge key={companyId} variant="secondary" className="flex items-center gap-1">
                    {company.societe_nom}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeCompany(companyId)}
                    />
                  </Badge>
                ) : null;
              })}
            </div>
          </div>
        )}

        {/* Criteria Selection */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Critères à comparer</Label>
            <div className="flex gap-1">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => onCriteriaChange(criteriaOptions.map(c => c.key))}
              >
                Tout sélectionner
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={() => onCriteriaChange([])}
              >
                Tout désélectionner
              </Button>
            </div>
          </div>
          <ScrollArea className="h-40 border rounded-md p-2">
            <div className="space-y-2">
              {criteriaOptions.map(criteria => (
                <div key={criteria.key} className="flex items-center space-x-2">
                  <Checkbox
                    id={`criteria-${criteria.key}`}
                    checked={selectedCriteria.includes(criteria.key)}
                    onCheckedChange={(checked) => handleCriteriaToggle(criteria.key, !!checked)}
                  />
                  <Label htmlFor={`criteria-${criteria.key}`} className="text-sm">
                    {criteria.label}
                  </Label>
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Selected Criteria Display */}
        {selectedCriteria.length > 0 && (
          <div className="space-y-2">
            <Label>Critères sélectionnés ({selectedCriteria.length})</Label>
            <div className="flex flex-wrap gap-2">
              {selectedCriteria.map(criteriaKey => {
                const criteria = criteriaOptions.find(c => c.key === criteriaKey);
                return criteria ? (
                  <Badge key={criteriaKey} variant="outline" className="flex items-center gap-1">
                    {criteria.label}
                    <X
                      className="h-3 w-3 cursor-pointer"
                      onClick={() => removeCriteria(criteriaKey)}
                    />
                  </Badge>
                ) : null;
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};